// src/services/estoqueService.js
import { 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  deleteDoc,
  increment, 
  serverTimestamp, 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  writeBatch, 
  addDoc 
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { arredondar } from "utils/mathUtils";
import { CNPJ_ECOPARQUE } from "config/constants";

/**
 * Atualiza o estoque no banco de dados com base na leitura de produtos do XML.
 *
 * @param {object} parsedData Objeto completo retornado pelo xmlParser (produtos, tpNF, dhEmi, vNF, chaveAcesso).
 */
export const atualizarEstoqueViaXML = async (parsedData) => {
  try {
    const { produtos, dhEmi, vNF, chaveAcesso, cnpjEmitente, cnpjDestinatario } = parsedData;
    
    // Verificação de segurança: A nota já foi importada?
    if (!chaveAcesso) {
      throw new Error("Chave de acesso ausente no XML.");
    }
    const historicoRef = doc(db, "historico_notas", chaveAcesso);
    const historicoDoc = await getDoc(historicoRef);
    if (historicoDoc.exists()) {
      throw new Error("Esta Nota Fiscal já foi importada anteriormente.");
    }

    // Identificação de Entrada ou Saída com base no CNPJ Emitente
    let tipoOperacao;
    let multiplicador;
    
    if (cnpjEmitente === CNPJ_ECOPARQUE) {
       tipoOperacao = "Saída";
       multiplicador = -1;
    } else {
       tipoOperacao = "Entrada";
       multiplicador = 1;
       
       // Validação de segurança para Entradas
       if (cnpjDestinatario !== CNPJ_ECOPARQUE) {
           throw new Error("A nota foi emitida para outra empresa por engano (CNPJ Destinatário incorreto).");
       }
    }

    let atualizadosCount = 0;

    // NOVO: Verificação de Mapeamento para Entradas
    let pendingMapping = [];
    const produtosMapeados = [];

    if (tipoOperacao === "Entrada") {
      if (!parsedData.cnpjEmitente) {
          throw new Error("CNPJ do emitente não encontrado. Impossível mapear fornecedor.");
      }

      // Buscar os vínculos para cada produto
      for (const produto of produtos) {
          const mappingId = `${parsedData.cnpjEmitente}_${produto.codigo}`;
          const mappingRef = doc(db, "mapeamento_produtos", mappingId);
          const mappingDoc = await getDoc(mappingRef);

          if (mappingDoc.exists()) {
              // Substitui o código do fornecedor pelo nosso código interno para a gravação no estoque
              const mappingData = mappingDoc.data();
              const interno = mappingData.codigo_estoque_interno;
              const categoria = mappingData.categoria_contabil || "N/A";
              const fator = Number(mappingData.fator_conversao) || produto.fatorConversaoSugerido || 1;
              produtosMapeados.push({ 
                ...produto, 
                codigoInterno: interno, 
                categoriaContabil: categoria,
                fatorConversao: fator
              });
          } else {
              // Verifica NCM para saber se é Bebida (NCM começando com 22)
              const isBebida = produto.ncm && produto.ncm.startsWith("22");
              
              if (isBebida) {
                  // Produto é bebida e ainda não foi pareado humanamente, joga para o modal
                  pendingMapping.push({ 
                     codigoFornecedor: produto.codigo, 
                     nomeFornecedor: produto.nome,
                     ncm: produto.ncm,
                     cnpjEmitente: parsedData.cnpjEmitente,
                     uCom: produto.uCom || "UN",
                     qCom: produto.qCom || produto.quantidade || 0,
                     uTrib: produto.uTrib || "",
                     qTrib: produto.qTrib || 0,
                     fatorConversaoSugerido: produto.fatorConversaoSugerido || 1
                  });
              } else {
                  // É carne, limpeza, sorvete, etc. Bypass automático direto para o financeiro.
                  produtosMapeados.push({ 
                      ...produto, 
                      codigoInterno: "DESP_GERAL", 
                      categoriaContabil: "INSUMO",
                      fatorConversao: 1
                  });
              }
          }
      }

      // Se houver pendências, aborta e devolve a lista para o Front-end lidar com o Modal
      if (pendingMapping.length > 0) {
          return {
              success: false,
              pendingMapping: true,
              failedItems: pendingMapping,
              message: "Aguardando intervenção humana para mapeamento de insumos."
          };
      }
    } else {
       // Saídas não exigem mapeamento (assumimos que já usam código interno na emissão)
       for (const produto of produtos) {
           produtosMapeados.push({ 
             ...produto, 
             codigoInterno: produto.codigo, 
             categoriaContabil: "SAIDA_DIRETA",
             fatorConversao: 1
           });
       }
    }

    // Buscar cache de estoque para validar flags de segurança (ex: ignorarEstoque)
    const cacheEstoque = {};
    for (const p of produtosMapeados) {
        if (!cacheEstoque[p.codigoInterno]) {
            const docSnap = await getDoc(doc(db, "estoque", p.codigoInterno));
            if (docSnap.exists()) {
                cacheEstoque[p.codigoInterno] = docSnap.data();
            }
        }
    }

    // Inicializa o Batch (operações atômicas)
    const batch = writeBatch(db);

    // AÇÃO A: Preparar atualizações de estoque no batch
    produtosMapeados.forEach((produto) => {
      if (produto.quantidade <= 0) return;

      // REGRA: Ignorar controle físico se a flag estiver ativada
      const docCache = cacheEstoque[produto.codigoInterno];
      if (docCache && docCache.ignorarEstoque) {
          return; // Pula a alteração no documento "estoque", não desconta saldo
      }

      // REGRA FINANCEIRA: Só atualiza contagem de estoque físico se for "REVENDA" ou Saída
      if (tipoOperacao === "Entrada" && produto.categoriaContabil !== "REVENDA") {
          return; // Pula a alteração no documento "estoque"
      }

      const fator = Number(produto.fatorConversao) || 1;
      const quantidadeEstoque = arredondar(produto.quantidade * fator);
      const deltaEstoque = arredondar(quantidadeEstoque * multiplicador);
      const produtoDocRef = doc(db, "estoque", produto.codigoInterno);

      const updateData = {
          quantidadeAtual: increment(deltaEstoque),
          ultimaMovimentacao: serverTimestamp(),
      };

      // Apenas garantimos a criação do cadastro base (nome, código) 
      // se for uma Saída direta (onde o nome original da nota é o nosso padrão)
      if (tipoOperacao === "Saída") {
          updateData.codigo = produto.codigoInterno;
          updateData.nome = produto.nome;
          updateData.ean = produto.ean || "";
          updateData.ncm = produto.ncm || "N/A";
      }

      batch.set(
        produtoDocRef,
        updateData,
        { merge: true }
      );
      atualizadosCount++;
    });

    // AÇÃO B: Criar registro no histórico usando a chave de acesso como ID
    batch.set(historicoRef, {
      chave_acesso: chaveAcesso,
      tipo_operacao: tipoOperacao,
      data_emissao: dhEmi, // String ISO ou vinda do XML
      valor_total: vNF,
      itens: produtosMapeados.map(p => {
        const fator = Number(p.fatorConversao) || 1;
        const qtdOriginal = p.quantidade || 0;
        const qtdEstoque = arredondar(qtdOriginal * fator);
        return {
          codigo: p.codigo, // Código original do fornecedor
          nome: p.nome,
          uCom: p.uCom || "UN",
          quantidade_original: qtdOriginal, // Quantidade de caixas/embalagens na NF
          fator_conversao: fator, // Fator multiplicador para o estoque
          quantidade_estoque: qtdEstoque, // Quantidade física real de unidades
          codigo_interno: p.codigoInterno || "",
          valor_produto: p.vProd || 0,
          cfop: p.cfop || "",
          categoria_contabil: p.categoriaContabil
        };
      }),
      data_registro: serverTimestamp()
    });

    // Executar todas as operações de uma vez (All-or-Nothing)
    await batch.commit();

    return { 
        success: true, 
        message: `${atualizadosCount} itens atualizados (Operação de ${tipoOperacao}).`
    };
  } catch (error) {
    console.error("Erro no EstoqueService:", error);
    return { success: false, message: error.message || "Erro de conexão com o banco ao atualizar estoque." };
  }
};

/**
 * Escuta as atualizações da coleção de estoque em tempo real.
 *
 * @param {function} callback Função a ser chamada passando o array de produtos atualizado sempre que o banco mudar.
 * @returns {function} unsubscribe Função para cancelar a escuta (bom para useEffect cleanup).
 */
export const observarEstoque = (callback) => {
  const estoqueRef = collection(db, "estoque");
  const q = query(estoqueRef, orderBy("nome", "asc"));

  // onSnapshot escuta o banco em tempo real
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const lista = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      
      // Formata a data (se existir)
      let dataFormatada = "Data indisponível";
      if (data.ultimaMovimentacao) {
        // ultimaMovimentacao pode ser null se o serverTimestamp acabou de ser disparado e a latência ainda não converteu, 
        // ou um objeto Timestamp. Precisamos lidar com ambos confiantemente.
        const dateObj = data.ultimaMovimentacao.toDate ? data.ultimaMovimentacao.toDate() : new Date();
        dataFormatada = dateObj.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      }

      const isBebida = data.ncm && String(data.ncm).startsWith("22");
      const isAlmoco = (data.codigo && data.codigo === "00000001") || docSnap.id === "00000001" || (data.nome && data.nome.toUpperCase().includes("ALMOCO"));
      lista.push({
        id: docSnap.id, // o id do documento (no caso cProd)
        codigo: data.codigo || docSnap.id,
        nome: data.nome,
        ncm: data.ncm || (isAlmoco ? "21069090" : "N/A"),
        cfop: data.cfop || (isBebida ? "5405" : "5102"),
        csosn: data.csosn || (isBebida ? "500" : (isAlmoco ? "103" : "102")),
        cest: data.cest || "",
        quantidadeAtual: data.quantidadeAtual || 0,
        precoFiscal: data.precoFiscal || 0,
        ultimaAtualizacao: dataFormatada
      });
    });

    // Chama o callback passando a lista de volta pro componente
    callback(lista);
  }, (error) => {
    console.error("Erro ao escutar o estoque: ", error);
  });

  return unsubscribe;
};

/**
 * Obtém o resumo financeiro das notas no período especificado.
 *
 * @param {number} mes (0-11) Mês do filtro
 * @param {number} ano Ano do filtro
 * @returns {Promise<object>} Objeto com totais { entradas: number, saidas: number }
 */
export const obterResumoFinanceiroEstoque = async (mes, ano) => {
  try {
    const historicoRef = collection(db, "historico_notas");
    
    // Como data_emissao está gravado como string ISO (YYYY-MM-DDTHH:mm:ss.sssZ),
    // podemos filtrar usando strings de começo e fim do mês.
    // Ex: 2023-10-01T00:00:00.000Z até 2023-10-31T23:59:59.999Z
    const startDate = new Date(ano, mes, 1).toISOString();
    const endDate = new Date(ano, mes + 1, 0, 23, 59, 59, 999).toISOString();

    const q = query(
      historicoRef,
      // Usaremos apenas getDocs e filtraremos as datas localmente caso não haja índice criado,
      // para evitar erros de deploy "missing index" imediatos no Firebase. 
      // Em produção massiva, o ideal é compor orderBy e where, exigindo índice.
    );

    const snapshot = await import("firebase/firestore").then(m => m.getDocs(q));

    let totalEntradas = 0;
    let totalSaidas = 0;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const emissao = data.data_emissao;

      // Filtro local pela data do mês solicitado
      if (emissao >= startDate && emissao <= endDate) {
        if (data.tipo_operacao === "Entrada" || data.tipo_operacao === "0") {
          totalEntradas += (data.valor_total || 0);
        } else if (data.tipo_operacao === "Saída" || data.tipo_operacao === "1") {
          totalSaidas += (data.valor_total || 0);
        }
      }
    });

    return { entradas: totalEntradas, saidas: totalSaidas };
  } catch (error) {
    console.error("Erro ao obter resumo financeiro:", error);
    return { entradas: 0, saidas: 0 };
  }
};

/**
 * Salva a decisão do usuário mapeando o código do produto do fornecedor para um item interno.
 * 
 * @param {string} cnpj CNPJ do fornecedor.
 * @param {string} cProdFornecedor Código original do produto na NFe.
 * @param {string} codigoInterno O ID do documento na coleção `estoque`.
 * @param {string} categoriaContabil A classificação financeira do produto.
 * @param {number} fatorConversao Multiplicador da embalagem (ex: 12 para dúzia/pack de 12).
 * @param {string} nomeFornecedor Nome do produto na nota fiscal.
 * @param {string} uCom Unidade comercial da nota (ex: EB, DZ, cx012).
 */
export const salvarMapeamento = async (
  cnpj, 
  cProdFornecedor, 
  codigoInterno, 
  categoriaContabil = "INSUMO",
  fatorConversao = 1,
  nomeFornecedor = "",
  uCom = "UN"
) => {
  const mappingId = `${cnpj}_${cProdFornecedor}`;
  const mappingRef = doc(db, "mapeamento_produtos", mappingId);
  
  await setDoc(mappingRef, {
    cnpj_fornecedor: cnpj,
    codigo_fornecedor: cProdFornecedor,
    nome_produto_fornecedor: nomeFornecedor || "",
    unidade_fornecedor: uCom || "UN",
    codigo_estoque_interno: codigoInterno,
    categoria_contabil: categoriaContabil,
    fator_conversao: Number(fatorConversao) || 1,
    data_mapeamento: serverTimestamp()
  }, { merge: true });
};

/**
 * Obtém todos os mapeamentos cadastrados no dicionário.
 */
export const obterTodosMapeamentos = async () => {
  try {
    const ref = collection(db, "mapeamento_produtos");
    const snap = await getDocs(ref);
    const lista = [];
    snap.forEach((d) => {
      lista.push({ id: d.id, ...d.data() });
    });
    return lista;
  } catch (error) {
    console.error("Erro ao carregar mapeamentos:", error);
    return [];
  }
};

/**
 * Exclui um vínculo de mapeamento do dicionário.
 */
export const excluirMapeamento = async (mappingId) => {
  const ref = doc(db, "mapeamento_produtos", mappingId);
  await deleteDoc(ref);
  return { success: true };
};

/**
 * Obtém todas as notas registradas no histórico para auditoria.
 */
export const obterHistoricoNotasCompleto = async () => {
  try {
    const historicoRef = collection(db, "historico_notas");
    const snap = await getDocs(historicoRef);
    const lista = [];
    snap.forEach((d) => {
      lista.push({ id: d.id, ...d.data() });
    });
    lista.sort((a, b) => new Date(b.data_emissao || 0) - new Date(a.data_emissao || 0));
    return lista;
  } catch (error) {
    console.error("Erro ao obter histórico de notas:", error);
    return [];
  }
};

/**
 * Aplica um ajuste corretivo atômico no estoque para uma nota fiscal processada anteriormente.
 */
export const aplicarAjusteEstoqueNota = async (chaveAcesso, codigoInterno, deltaUnidades, nomeItem, codigoFornecedor) => {
  try {
    const batch = writeBatch(db);
    
    // 1. Incrementa o saldo real no estoque com merge seguro
    const estoqueRef = doc(db, "estoque", codigoInterno);
    batch.set(estoqueRef, {
      quantidadeAtual: increment(deltaUnidades),
      ultimaMovimentacao: serverTimestamp()
    }, { merge: true });

    // 2. Atualiza o registro no historico_notas
    const historicoRef = doc(db, "historico_notas", chaveAcesso);
    const histDoc = await getDoc(historicoRef);
    if (histDoc.exists()) {
      const data = histDoc.data();
      const itensAtualizados = (data.itens || []).map(it => {
        const isMatch =
          (codigoFornecedor && String(it.codigo).trim() === String(codigoFornecedor).trim()) ||
          it.codigo_interno === codigoInterno ||
          (it.nome && nomeItem && it.nome.trim().toLowerCase() === nomeItem.trim().toLowerCase()) ||
          it.codigo === codigoInterno;

        if (isMatch) {
          return {
            ...it,
            codigo_interno: codigoInterno,
            categoria_contabil: "REVENDA",
            quantidade_estoque: (it.quantidade_estoque || 0) + deltaUnidades,
            ajuste_conversao_aplicado: true,
            delta_ajustado: deltaUnidades,
            ajustado_em: new Date().toISOString()
          };
        }
        return it;
      });
      batch.update(historicoRef, { 
        itens: itensAtualizados,
        ultimo_ajuste_estoque: serverTimestamp()
      });
    }

    await batch.commit();
    return { success: true, message: `Estoque ajustado em +${deltaUnidades} unidades!` };
  } catch (error) {
    console.error("Erro ao aplicar ajuste de estoque:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Fecha a temporada atual do estoque, salvando um retrato histórico e zerando as quantidades.
 * @param {string} nomeTemporada O nome definido pelo admin para a temporada que está sendo fechada.
 */
export const fecharTemporadaEstoque = async (nomeTemporada) => {
  try {
    const estoqueRef = collection(db, "estoque");
    const snapshot = await getDocs(estoqueRef);
    
    const itens = [];
    const batch = writeBatch(db);
    
    // Ler todos os itens atuais e preparar o zeramento
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      itens.push({
        id: docSnap.id,
        codigo: data.codigo || "",
        nome: data.nome || "",
        ncm: data.ncm || "N/A",
        quantidadeAtual: data.quantidadeAtual || 0,
      });
      
      // Zera a quantidade atual para a próxima temporada, mas mantém o cadastro
      batch.update(docSnap.ref, { 
        quantidadeAtual: 0,
        ultimaMovimentacao: serverTimestamp()
      });
    });

    // Salvar o retrato na coleção historico_temporadas
    const historicoTemporadasRef = collection(db, "historico_temporadas");
    await addDoc(historicoTemporadasRef, {
      nome: nomeTemporada,
      dataFechamento: serverTimestamp(),
      itens: itens
    });

    // Commit de todas as atualizações de zeramento
    await batch.commit();

    return { success: true, message: "Temporada fechada e estoque zerado com sucesso." };
  } catch (error) {
    console.error("Erro ao fechar temporada:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Retorna a lista de todas as temporadas fechadas.
 */
export const obterTemporadasFechadas = async () => {
  try {
    const historicoTemporadasRef = collection(db, "historico_temporadas");
    const q = query(historicoTemporadasRef, orderBy("dataFechamento", "desc"));
    const snapshot = await getDocs(q);
    
    const temporadas = [];
    snapshot.forEach((docSnap) => {
      temporadas.push({
        id: docSnap.id,
        ...docSnap.data()
      });
    });
    
    return temporadas;
  } catch (error) {
    console.error("Erro ao obter temporadas fechadas:", error);
    return [];
  }
};

/**
 * Reabre uma temporada fechada, restaurando as quantidades e apagando o histórico.
 * @param {string} temporadaId ID do documento na coleção historico_temporadas
 */
export const reabrirTemporadaEstoque = async (temporadaId) => {
  try {
    const historicoRef = doc(db, "historico_temporadas", temporadaId);
    const historicoDoc = await getDoc(historicoRef);
    
    if (!historicoDoc.exists()) {
      return { success: false, message: "Temporada não encontrada." };
    }

    const data = historicoDoc.data();
    const itens = data.itens || [];
    const batch = writeBatch(db);

    // Restaura as quantidades no estoque
    for (const item of itens) {
      if (item.id) {
        const estoqueRef = doc(db, "estoque", item.id);
        batch.update(estoqueRef, { 
          quantidadeAtual: item.quantidadeAtual || 0,
          ultimaMovimentacao: serverTimestamp()
        });
      }
    }

    // Deleta o registro histórico
    batch.delete(historicoRef);

    await batch.commit();

    return { success: true, message: "Temporada reaberta com sucesso!" };
  } catch (error) {
    console.error("Erro ao reabrir temporada:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Permite ao admin ajustar manualmente o saldo físico de um produto no estoque.
 */
export const ajustarSaldoManualEstoque = async (codigoItem, novaQuantidade, motivo = "Ajuste manual de inventário", ignorarEstoque = false) => {
  try {
    const estoqueRef = doc(db, "estoque", codigoItem);
    await setDoc(estoqueRef, {
      quantidadeAtual: Number(novaQuantidade),
      ultimaMovimentacao: serverTimestamp(),
      ultimo_motivo_ajuste: motivo,
      ignorarEstoque: ignorarEstoque,
    }, { merge: true });

    return { success: true, message: "Saldo do estoque atualizado com sucesso!" };
  } catch (error) {
    console.error("Erro ao ajustar saldo manual do estoque:", error);
    return { success: false, message: error.message || "Falha ao atualizar saldo." };
  }
};

/**
 * Salva o preço fiscal de um produto diretamente no estoque para persistência no módulo fiscal.
 */
export const salvarPrecoFiscal = async (codigoItem, preco) => {
  try {
    if (!preco || isNaN(preco)) return { success: false };
    const estoqueRef = doc(db, "estoque", codigoItem);
    await setDoc(estoqueRef, {
      precoFiscal: Number(preco)
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error("Erro ao salvar preço fiscal:", error);
    return { success: false, message: error.message };
  }
};

/**
 * Salva parâmetros fiscais (CFOP, CSOSN, CEST) de um item no estoque.
 */
export const salvarParametrosFiscais = async (codigoItem, parametros) => {
  try {
    const estoqueRef = doc(db, "estoque", codigoItem);
    await setDoc(estoqueRef, parametros, { merge: true });
    return { success: true };
  } catch (error) {
    console.error("Erro ao salvar parâmetros fiscais:", error);
    return { success: false, message: error.message };
  }
};


